import { Button, CircularProgress, TextField, WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { CancelOutlined } from '@material-ui/icons';
import { Autocomplete } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import Select from 'react-select';
import { CustomerContractStatusOptions } from '../../codec/customerContractInDTODecoder';
import { ICustomerContractSearchDTO } from '../../repository/jago/model/input/ICustomerContractSearchDTO';
import { ICustomerOptionDTO } from '../../repository/jago/model/input/ICustomerOptionDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';
import Permission from '../Permission/Permission';
import ReduxLanguage from '../ReduxLanguage';
import { IUserOptionDTO } from '../../repository/jago/model/input/IUserOptionDTO';
import { IDemoPlantSearchDTO } from '../../repository/jago/model/input/IDemoPlantSearchDTO';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    padding: 10,
  },
  logo: {
    margin: 5,
    marginBottom: 15,
    alignSelf: 'center',
    height: 45,
  },
  text: {
    margin: 0,
    marginBottom: 0,
  },
  innerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
    marginLeft: 10,
  },
  title: {
    color: 'white',
  },
  circularProgress: {
    color: 'white',
  },
});


export interface IDemoPlantSearchBarProps extends WithStyles<typeof styles> {
  onSearch: (searchValues: IDemoPlantSearchDTO) => void;
  onResetSearch: () => void;
  onCreateNewEntityButtonClick: () => void;
  hideNewButton?: boolean;
  onFilterUsers: (value: string) => void;
  onFilterDomains: (value: string) => void;
  onExportCSV: () => void;
  usersOptions: IUserOptionDTO[];
  plantsOptions: IDomainOptionDTO[];
}

export interface IDemoPlantSearchBarOption {
  label: string;
  value: string;
  original: string;
}

export interface IDemoPlantSearchBarState {
  searchValues: Record<keyof IDemoPlantSearchDTO, string>;
  plantSelected: IDomainOptionDTO | undefined;
  userSelected: IUserOptionDTO | undefined;
  searchUserText: string;
  searchDomainText: string;
}

class DemoPlantSearchBar extends React.Component<IDemoPlantSearchBarProps, IDemoPlantSearchBarState> {
  constructor(props: IDemoPlantSearchBarProps) {
    super(props);
    this.state = {
      searchValues: {
        userId: '',
        plantId: '',
        name: '',
      },
      plantSelected: undefined,
      userSelected: undefined,
      searchUserText: '',
      searchDomainText: '',
    }
  }

  public onSetSearchValue(field: keyof IDemoPlantSearchDTO, value: string) {
    const { searchValues } = this.state;
    const { onSearch } = this.props;
    searchValues[field] = value ? value.toString() : '';
    this.setState({ searchValues });
    onSearch(searchValues);
  }

  public onSearch() {
    const { onSearch } = this.props;
    onSearch(this.state.searchValues);
  }


  public onResetFields() {
    const { onResetSearch } = this.props;

    this.setState({ 
      searchValues: { userId: '', plantId: '', name: '' }, 
      userSelected: undefined,
      plantSelected: undefined,
      searchDomainText: '',
      searchUserText: '',
    }, () => {
      onResetSearch();
    });
  }

  public onSelectUser(user: IUserOptionDTO) {
    if (user && user.value) {
      this.onSetSearchValue('userId', user.value.id.toString());
      this.setState({ userSelected: user });
    } else {
      this.onSetSearchValue('userId', undefined);
      this.setState({ userSelected: undefined, searchUserText: '' });
    }
    setTimeout(() => this.onSearch(), 200);

  }

  public onSelectPlant(plant: IDomainOptionDTO) {
    if (plant && plant.value) {
      this.onSetSearchValue('plantId', plant.value.id.toString());
      this.setState({ plantSelected: plant });
    } else {
      this.onSetSearchValue('plantId', undefined);
      this.setState({ plantSelected: undefined, searchDomainText: '' });
    }
    setTimeout(() => this.onSearch(), 200);
  }

  public render() {
    const { classes, onCreateNewEntityButtonClick, hideNewButton, usersOptions, plantsOptions, onExportCSV } = this.props as any;
    const { searchValues, userSelected, plantSelected,  searchUserText, searchDomainText } = this.state;
    const debouncedSearch = _.debounce((text) => this.props.onFilterUsers(text), 500);
    const debouncedDomainsSearch = _.debounce((text) => this.props.onFilterDomains(text), 500);


    return (
      <div className={classes.container}>
        <div style={{ marginLeft: 0, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <TextField
            id="outlined-basic"
            label={<ReduxLanguage languageKey="gateways.name" /> }
            variant="outlined"
            fullWidth
            type="search"
            style={{ marginRight: 10, minWidth: 180 }}
            value={searchValues.name}
            onChange={e => this.onSetSearchValue('name', e.target.value)}
          />
          <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_DEMO_CREATE}>
            <Autocomplete
              options={usersOptions}
              style={{ minWidth: 250, }}
              getOptionLabel={(option) => option.label}
              inputValue={userSelected ? userSelected.label : searchUserText}
              value={userSelected}
              placeholder={<ReduxLanguage languageKey={'forms.selectUser'} /> as any}
              isClearable
              autoComplete
              onChange={(e, value) => {this.onSelectUser(value)}}
              onInputChange={(event, newInputValue) => { 
                this.setState({ searchUserText: newInputValue }); 
                debouncedSearch(newInputValue); 
              }}
              renderInput={(params) => <TextField {...params} variant="outlined" style={{ width: 250 }} label={<ReduxLanguage languageKey="forms.user" />}/>}
            />
          </Permission>
          <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_DEMO_CREATE}>
            <Autocomplete
              options={plantsOptions}
              style={{ minWidth: 250, marginLeft: 10 }}
              getOptionLabel={(option) => option.label}
              inputValue={plantSelected ? plantSelected.label : searchDomainText}
              value={plantSelected}
              placeholder={<ReduxLanguage languageKey={'forms.selectPlant'} /> as any}
              isClearable
              autoComplete
              onChange={(e, value) => {this.onSelectPlant(value)}}
              onInputChange={(event, newInputValue) => { 
                this.setState({ searchDomainText: newInputValue }); 
                debouncedDomainsSearch(newInputValue); 
              }}
              renderInput={(params) => <TextField {...params} variant="outlined" style={{ width: 250 }} label={<ReduxLanguage languageKey="domains.domainName" />}/>}
            />
          </Permission>
          <Button
            variant="contained"
            style={{ backgroundColor: 'red', color: 'white', marginLeft: 10, fontWeight: 'bold'  }}
            onClick={() => this.onResetFields()}
          >
            <CancelOutlined style={{ color: 'white' }} />
          </Button>
          <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_DEMO_CREATE}>
            <Button
              variant="contained"
              style={{ backgroundColor: '#5AC0B1', color: 'white', width: '100%',  height: 40, marginLeft: 40, minWidth: 100, fontSize: 13, fontWeight: 'bold'  }}
              onClick={() => onCreateNewEntityButtonClick()}
            >
              <ReduxLanguage languageKey="forms.createNewCustomer" />
            </Button>
          </Permission>
          <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_DEMO_MANAGEMENT}>
            <Button
              variant="contained"
              style={{ backgroundColor: 'cornflowerblue', color: 'white', width: '100%',  height: 40, marginLeft: 40, minWidth: 100, fontSize: 13, fontWeight: 'bold'  }}
              onClick={() => onExportCSV()}
            >
              <ReduxLanguage languageKey="recharges.exportRechargesCSV" />
            </Button>
        </Permission>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(DemoPlantSearchBar);
